import { Layout as AntLayout, Button } from 'antd'
import {
  HomeOutlined,
  HeartOutlined,
  SettingOutlined,
  OrderedListOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../utils/redux/store'
import './MainLayout.scss'
import LogoImage from '../assets/logo-image.png'
import { useEffect, useMemo, useState } from 'react'
import { attemptGetFavs } from '../utils/redux/favsSlice'
import UploadedImage from './PlaylistImage'

const { Sider, Content } = AntLayout

function playlistImageUrl(image: string) {
  return `${process.env.REACT_APP_UPLOADED_BASE_URL}/playlists/${image}`
}

export default function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t } = useTranslation('common')

  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    dispatch(attemptGetFavs())
  }, [dispatch])

  // ✅ нормальное определение mobile без зависимости от Sider breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const { songs, playlists } = useAppSelector((state) => state.favs)

  const menuItems = useMemo(
    () => [
      {
        key: '/home',
        icon: <HomeOutlined />,
        label: t('home'),
        onClick: () => navigate('/home'),
      },
      // {
      //   key: '/library',
      //   icon: <HeartOutlined />,
      //   label: t('library'),
      //   onClick: () => navigate('/library'),
      // },
      {
        key: '/orders',
        icon: <ShoppingCartOutlined />,
        label: t('Orders'),
        onClick: () => navigate('/orders'),
      },
      {
        key: '/settings',
        icon: <SettingOutlined />,
        label: t('settings'),
        onClick: () => navigate('/settings'),
      },
    ],
    [navigate, t],
  )

  const selectedKey =
    menuItems.find((item) => location.pathname.startsWith(item.key))?.key || '/home'

  return (
    <AntLayout className="main-layout">
      {/* ✅ DESKTOP: сайдбар как был */}
      {!isMobile && (
        <Sider className="main-sider" width={300}>
          <img src={LogoImage} alt="MusicTMM" className="sider-logo" />

          <div className="sider-menu">
            {menuItems.map((item) => (
              <Button
                key={item.key}
                type={selectedKey === item.key ? 'primary' : 'text'}
                onClick={() => item.onClick()}
                block
                className="sider-btn"
              >
                {item.icon}
                <span>{item.label}</span>
              </Button>
            ))}
          </div>

          <div className="liked-title">{t('Liked Music')}</div>
          <div className="liked-section">
            {songs?.map((song) => (
              <div key={song._id} className="liked-item">
                <UploadedImage
                  src={song.imageUrl}
                  fallback="/icons/heart.svg"
                  style={{
                    backgroundColor: '#1c1c1c',
                    borderRadius: 4,
                    width: 32,
                    height: 32,
                    marginRight: 8,
                    verticalAlign: 'middle',
                  }}
                />
                <div className="liked-text">
                  <div className="liked-song-title">{song.title}</div>
                  <div className="liked-song-artist">{song.artist}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="liked-title">{t('Liked Playlists')}</div>
          <div className="liked-section">
            {playlists?.map((playlist) => (
              <div key={playlist._id} className="liked-item">
                <UploadedImage
                  src={playlistImageUrl(playlist.image)}
                  fallback="/icons/heart.svg"
                  style={{
                    backgroundColor: '#1c1c1c',
                    borderRadius: 4,
                    width: 32,
                    height: 32,
                    marginRight: 8,
                    verticalAlign: 'middle',
                  }}
                />
                <div className="liked-text">
                  <div className="liked-song-title">{String(playlist.title ?? '')}</div>
                </div>
              </div>
            ))}
          </div>
        </Sider>
      )}

      <Content className="main-content">
        <Outlet />

        {/* ✅ MOBILE: bottom bar вместо сайдбара */}
        {isMobile && (
          <div className="bottomDockShell" aria-label="Bottom navigation shell">
            {/* ✅ сюда на мобилке будет портальиться GlobalAudioPlayer */}
            <div id="ga-mobile-slot" className="bbPlayerSlot" />

            <nav className="bottomDock" aria-label="Bottom navigation">
              {menuItems.map((item) => {
                const isActive = selectedKey === item.key
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`bbItem ${isActive ? 'isActive' : ''}`}
                    onClick={item.onClick}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="bbIcon" aria-hidden="true">
                      {item.icon}
                    </span>
                    <span className="bbLabel">{item.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>
        )}
      </Content>
    </AntLayout>
  )
}
